import React, { useState, useEffect, useRef, useMemo } from "react";
import { T } from "../lib/theme";
import { num, fmt, daysUntil, requestFullAccess } from "../lib/utils";
import { resolveValue, resolveBestOffer, resolveInsuranceQuote, resolveRefiRate } from "../lib/valuation";
import { fetchFuelEconomy } from "../lib/fuelEconomy";
import { resolveSchedule } from "../lib/maintenanceSchedule";
import { getShops, rankShops } from "../lib/serviceBooking";
import { SectionLabel, MetaChip, Spin, ModeBadge, ProvenanceBadge } from "./Atoms";
import { MaintenanceSchedule } from "./MaintenanceSchedule";
import { SafetyProfile } from "./SafetyProfile";
import { VehicleModal } from "./VehicleModal";
import { BidModal } from "./BidModal";
import { RecallAndService } from "./DealerFinder";

// Module-level, not component state: the FRED national-average rate is
// the same number for every vehicle in the household, not per-vehicle
// data — fetching it once per VehicleRow instance would mean N identical
// network calls for N vehicles. Every mounted VehicleRow awaits this same
// shared promise instead, so only one /api/fred-rate call ever fires per
// page load regardless of fleet size.
let fredRatePromise = null;
function getFredRate(){
  if(!fredRatePromise){
    fredRatePromise = fetch("/api/fred-rate").then(r=>r.ok?r.json():null).catch(()=>null);
  }
  return fredRatePromise;
}

// Same reasoning as getFredRate above — one EIA gas-price fetch per
// page load, not per vehicle card. Keyed by state (all mounted
// VehicleRow instances share the same household, so the same state
// value, within one page load) so a null/empty state consistently hits
// the same shared "national" promise rather than creating a fresh
// fetch per instance.
let gasPricePromises = {};
function getGasPrice(state){
  const key = state || "";
  if(!gasPricePromises[key]){
    const qs = key ? `?state=${key}` : "";
    gasPricePromises[key] = fetch(`/api/eia-gas-price${qs}`).then(r=>r.ok?r.json():null).catch(()=>null);
  }
  return gasPricePromises[key];
}

// Mileage now lives inline in the collapsed header (Decision 8) — a
// small pencil icon next to the plain mono number, not the old
// always-clickable "68,400 mi [Update]" badge pair, so it stops
// competing visually with the identity text and alerts sharing the
// same line. Same underlying edit/save state and validation as before.
function MileageUpdater({vehicle,onUpdate}){
  const[editing,setEditing]=useState(false);
  const[val,setVal]=useState(String(vehicle.mileage));
  const[saving,setSaving]=useState(false);
  const inputRef=useRef();
  useEffect(()=>{if(editing&&inputRef.current)inputRef.current.focus();},[editing]);
  const save=async()=>{
    const n=num(val);
    if(n>0&&n!==vehicle.mileage){
      setSaving(true);
      const ok=await onUpdate({mileage:n});
      setSaving(false);
      if(ok)setEditing(false);
    }else{
      setEditing(false);
    }
  };
  if(editing)return(
    <span style={{display:"inline-flex",alignItems:"center",gap:5}} onClick={e=>e.stopPropagation()}>
      <input ref={inputRef} type="number" inputMode="numeric" value={val} onChange={e=>setVal(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")save();if(e.key==="Escape")setEditing(false);}} style={{width:92,border:`1.5px solid ${T.teal}`,borderRadius:6,padding:"3px 7px",fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace",color:T.text,background:T.card,outline:"none"}}/>
      <button onClick={save} disabled={saving} style={{background:T.teal,color:"#fff",border:"none",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:700,opacity:saving?.6:1}}>{saving?"...":"Save"}</button>
      <button onClick={()=>setEditing(false)} disabled={saving} style={{background:T.slateL,color:T.textMd,border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 8px",fontSize:11,fontWeight:600}}>x</button>
    </span>
  );
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
      <span className="mono" style={{fontWeight:700,color:T.text}}>{num(vehicle.mileage).toLocaleString()} mi</span>
      <button onClick={e=>{e.stopPropagation();setEditing(true);}} title="Update mileage" style={{background:"none",border:"none",padding:0,color:T.textSm,fontSize:11,cursor:"pointer",lineHeight:1}}>✏️</button>
    </span>
  );
}

// "Xd ago" / "today" for a fetchedAt timestamp — local to this file
// since nothing else here needed a past-date formatter yet.
function agoLabel(iso){
  if(!iso)return"";
  const days=Math.floor((Date.now()-new Date(iso).getTime())/86400000);
  return days<=0?"today":days===1?"1 day ago":`${days} days ago`;
}

// Manual per-vehicle trigger for the server-side enrich endpoint
// (functions/api/vehicles/[id]/enrich.js). Demo-gated the same way
// Add Vehicle is: shown but disabled with explanatory copy, since this
// is the same paid-lookup path — the server enforces its own daily cap
// regardless, but the UI shouldn't invite spend while in Demo. Also
// disabled for example/seed vehicles (vehicle.isExample) — the server
// refuses those outright now, so this is about not showing a button
// that would just error, not an independent guardrail.
function EnrichPanel({vehicleId,mode,isExample,onEnrich}){
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  const[info,setInfo]=useState(""); // set when the server served a cached value instead of refetching — not an error
  const[capReason,setCapReason]=useState(""); // "demo_lifetime_cap" — a real limit, not a failure, so it gets its own nudge instead of the generic err banner
  const[goLiveSent,setGoLiveSent]=useState(false);
  const demo=mode==="demo";
  const disabled=isExample||demo||busy;
  const run=async()=>{
    setBusy(true);setErr("");setInfo("");setCapReason("");
    try{
      const res=await fetch(`/api/vehicles/${vehicleId}/enrich`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok){
        if(body.reason==="demo_lifetime_cap")setCapReason(body.reason);
        else throw new Error(body.error||"Refresh failed.");
      }else{
        onEnrich(vehicleId,body.results);
        const v=body.results?.value;
        if(v?.cached){
          setInfo(`Value last updated ${agoLabel(v.fetchedAt)} — refreshes automatically after ${v.refreshMinAgeDays} days or a ${v.refreshMileageThreshold.toLocaleString()}-mile change.`);
        }
      }
    }catch(e){setErr(e.message||"Refresh failed.");}
    setBusy(false);
  };
  return(
    <div style={{padding:"14px 16px",borderRadius:12,border:`1.5px dashed ${T.border}`,background:T.slateL}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Refresh Estimates</div>
          {isExample?<div style={{fontSize:11,color:T.textSm,marginTop:2}}>This is example data — refresh isn't available for it.</div>
          :demo&&<div style={{fontSize:11,color:T.textSm,marginTop:2}}>Refresh unlocks once your account is reviewed.</div>}
        </div>
        <button onClick={run} disabled={disabled} style={{fontSize:12,fontWeight:700,color:disabled?T.textSm:T.teal,background:disabled?T.btnDisabledBg:T.tealL,border:`1px solid ${disabled?T.border:T.tealBorder}`,borderRadius:7,padding:"7px 14px",flexShrink:0,cursor:disabled?"default":"pointer",opacity:disabled?.7:1}}>{busy?"Researching...":"Refresh"}</button>
      </div>
      {capReason==="demo_lifetime_cap"&&<div style={{fontSize:12,color:T.amber,background:T.amberL,border:`1px solid ${T.amberBorderStrong}`,borderRadius:8,padding:"9px 12px",marginTop:8,lineHeight:1.5}}>You've used your free value check. {goLiveSent?<span style={{color:T.teal,fontWeight:700}}>Request sent ✓</span>:<button onClick={()=>requestFullAccess().then(setGoLiveSent)} style={{background:"none",border:"none",padding:0,color:T.teal,fontWeight:700,textDecoration:"none",cursor:"pointer",font:"inherit"}}>Go live</button>} to refresh this anytime and add more vehicles.</div>}
      {err&&<div style={{fontSize:12,color:T.red,marginTop:8}}>{err}</div>}
      {info&&<div style={{fontSize:12,color:T.textMd,marginTop:8}}>{info}</div>}
    </div>
  );
}

const SECTIONS=[
  {key:"financial",label:"Financial"},
  {key:"safety",label:"Safety & Recalls"},
  {key:"fuel",label:"Fuel Economy"},
  {key:"dates",label:"Dates & Coverage"},
  {key:"maint",label:"Maintenance"},
];

export function VehicleRow({vehicle,maintenance,idx,onUpdate,onDelete,mode,onServiceSchedule,onEnrich,householdState,onTcoChange,initialTab,onTabChange}){
  // Single-open accordion (Decision 1): one active tab key per vehicle,
  // not a set/array — opening a new tab implicitly closes whichever was
  // open, since there's only ever one value here. Clicking the already-
  // active tab toggles it closed (sets null), same as the old whole-card
  // open/close button did. Seeded from initialTab (the saved display_prefs
  // value for this vehicle, if any) only on first mount — this component
  // owns the state from then on, reporting changes back up via
  // onTabChange rather than staying controlled by the parent.
  const[activeTab,setActiveTab]=useState(initialTab??null);
  const[hintTab,setHintTab]=useState(null); // which tab an alert badge is currently hovered over, for the highlight-the-target-tab affordance
  const[sellOpen,setSellOpen]=useState(false);const[editOpen,setEditOpen]=useState(false);
  const[finOpen,setFinOpen]=useState(null); // which finance-strip cell's detail is expanded ("insurance"|"refi"|"sell"|null)
  const[mpg,setMpg]=useState(null);const[mpgLoading,setMpgLoading]=useState(false);const[mpgError,setMpgError]=useState("");
  // mpg feeds both the Fuel Economy tab AND the Financial tab's Monthly
  // Cost of Ownership — fetched lazily the first time EITHER tab is
  // opened (not just Fuel Economy), so opening Financial first doesn't
  // leave the fuel line permanently blank for the rest of the session.
  useEffect(()=>{if((activeTab==="financial"||activeTab==="fuel")&&!mpg&&!mpgLoading&&!mpgError&&vehicle.make&&vehicle.model&&vehicle.year){setMpgLoading(true);fetchFuelEconomy(vehicle.year,vehicle.make,vehicle.model).then(data=>{setMpg(data);setMpgLoading(false);}).catch(e=>{setMpgError(e.message||"Fuel economy lookup failed");setMpgLoading(false);});}},[activeTab]);
  // undefined=not yet requested, null=unavailable (fetch failed or key
  // unconfigured), {rate,asOf}=resolved — the Refinance Loan detail
  // only renders the national-average line when this is truthy, so a
  // failure or still-loading state just omits that one line, same
  // graceful-degradation shape as mpgError above. Only needed by the
  // Financial tab's Refinance detail.
  const[fredRate,setFredRate]=useState(undefined);
  useEffect(()=>{if(activeTab==="financial"&&fredRate===undefined){getFredRate().then(d=>setFredRate(d||null));}},[activeTab]);
  // Same undefined/null/resolved shape as fredRate above. Same dual
  // trigger as mpg — Financial's TCO and Fuel Economy's cost-per-mile
  // both need it.
  const[gasPrice,setGasPrice]=useState(undefined);
  useEffect(()=>{if((activeTab==="financial"||activeTab==="fuel")&&gasPrice===undefined){getGasPrice(householdState).then(d=>setGasPrice(d||null));}},[activeTab]);
  const resolvedValue=useMemo(()=>resolveValue(vehicle),[vehicle.id,vehicle.value,vehicle.mileage,vehicle.year,vehicle.financials?.value]);
  const hasValue=resolvedValue.amount!=null;
  const adjValue=resolvedValue.amount??0;
  const adjDiff=adjValue-num(vehicle.value);
  const resolvedBestOffer=useMemo(()=>resolveBestOffer(vehicle,resolvedValue.amount),[vehicle.id,resolvedValue.amount,vehicle.financials?.best_offer]);
  const resolvedRefiRate=useMemo(()=>resolveRefiRate(vehicle),[vehicle.id,vehicle.year,vehicle.financials?.refi_rate]);
  const resolvedInsurance=useMemo(()=>resolveInsuranceQuote(vehicle),[vehicle.id,vehicle.insurance,vehicle.financials?.insurance_quote]);
  const equity=hasValue?adjValue-num(vehicle.loan):null;const hasLoan=num(vehicle.loan)>0;
  const rateKnown=num(vehicle.rate)>0;
  const estRate=rateKnown?num(vehicle.rate):7.5; // rough current-market placeholder until the real rate is entered
  const paymentKnown=num(vehicle.payment)>0;
  const estPayment=paymentKnown?num(vehicle.payment):(hasLoan?Math.round((num(vehicle.loan)*(estRate/100/12))/(1-Math.pow(1+estRate/100/12,-60))):0);
  const insuranceKnown=num(vehicle.insurance)>0;
  const estInsurance=insuranceKnown?num(vehicle.insurance):Math.round(num(adjValue)*0.012/12/10)*10;
  const refiSave=hasLoan?Math.max(0,Math.round((num(vehicle.loan)*((num(vehicle.rate)-resolvedRefiRate.amount)/100))/12)):0;
  const insSave=resolvedInsurance?Math.max(0,estInsurance-resolvedInsurance.amount):0;const totalSave=refiSave+insSave;

  // Monthly Cost of Ownership — pure composition of data already in this
  // component's own state/scope (mpg, gasPrice, resolvedInsurance,
  // estPayment), zero new fetches. Fuel: EIA's current price, flat
  // 12,000 mi/yr assumption already used elsewhere in this codebase.
  // Maintenance: SVC_SHOPS' oil/rotation/brake pricing only, amortized
  // using maintenanceSchedule.js's own real month interval — deliberately
  // partial, excluded from the total (via the null check below) rather
  // than folded in as $0 for any vehicle where even that limited pricing
  // isn't available.
  const tco=useMemo(()=>{
    const ANNUAL_MILES=12000;
    const fuel=(mpg&&mpg.comb&&gasPrice)?(ANNUAL_MILES/mpg.comb)*gasPrice.price/12:null;
    const insurance=resolvedInsurance?resolvedInsurance.amount:null;
    let maintenanceCost=null;
    const sched=resolveSchedule(vehicle.make,vehicle.model,vehicle.year).items;
    const shop=rankShops(getShops(vehicle),"oil")[0];
    if(shop){
      let sum=0,any=false;
      [["oil","oil"],["tires","rotate"],["brakes","brake"]].forEach(([schedId,priceKey])=>{
        const item=sched.find(i=>i.id===schedId);
        const price=shop.hfRate?.[priceKey];
        if(item&&item.mo&&price!=null){sum+=price/item.mo;any=true;}
      });
      if(any)maintenanceCost=sum;
    }
    const known=[fuel,estPayment,insurance,maintenanceCost].filter(v=>v!=null);
    const total=known.length?known.reduce((s,v)=>s+v,0):null;
    return{fuel,payment:estPayment,insurance,maintenance:maintenanceCost,total};
  },[mpg,gasPrice,estPayment,resolvedInsurance,vehicle.id,vehicle.make,vehicle.model,vehicle.year]);

  // Reports this vehicle's resolved TCO up to HomeFleetApp for the
  // fleet-wide Monthly Outlay figure (Decision 3) — vehicle-local
  // computation, just the final number handed up via callback, no
  // fuel/mpg fetching lifted to the parent. Only fires once tco.total
  // actually resolves to something (not on every render).
  useEffect(()=>{if(onTcoChange)onTcoChange(vehicle.id,tco.total);},[tco.total,vehicle.id]);

  const net=resolvedBestOffer?resolvedBestOffer.amount-num(vehicle.loan):null;
  const daysReg=daysUntil(vehicle.regExp);const daysWar=daysUntil(vehicle.warrantyExp);
  const upcoming=maintenance.filter(m=>{const d=daysUntil(m.dueDate);return d!==null&&d<=60;});
  const overdue=maintenance.filter(m=>{const d=daysUntil(m.dueDate);return d!==null&&d<0;});
  // Each alert now also carries a target tab key + hover tip, so the
  // collapsed header can (a) point at which tab has the detail and
  // (b) light that tab up while the badge is hovered.
  const alerts=[];
  if(daysReg!==null&&daysReg<=60)alerts.push({label:`Reg ${daysReg<=0?"expired":daysReg+"d"}`,color:daysReg<=30?T.red:T.amber,bg:daysReg<=30?T.redL:T.amberL,cls:daysReg<=30?"red":"amber",target:"dates",tip:"Expand Dates & Coverage to see details"});
  if(daysWar!==null&&daysWar<=90&&vehicle.warrantyExp)alerts.push({label:`Warranty ${daysWar<=0?"expired":daysWar+"d"}`,color:T.amber,bg:T.amberL,cls:"amber",target:"dates",tip:"Expand Dates & Coverage to see details"});
  if(overdue.length)alerts.push({label:`${overdue.length} overdue`,color:T.red,bg:T.redL,cls:"red",target:"maint",tip:"Expand Maintenance to see details"});
  if(upcoming.length&&!overdue.length)alerts.push({label:`${upcoming.length} svc soon`,color:T.amber,bg:T.amberL,cls:"amber",target:"maint",tip:"Expand Maintenance to see details"});
  if(num(vehicle.rate)>6.5&&hasLoan)alerts.push({label:"High rate",color:T.amber,bg:T.amberL,cls:"amber",target:"financial",tip:"Expand Financial to see details"});
  if(equity!=null&&equity<0)alerts.push({label:"Neg. equity",color:T.red,bg:T.redL,cls:"red",target:"financial",tip:"Expand Financial to see details"});

  const openTab=key=>setActiveTab(t=>{
    const next=t===key?null:key;
    if(onTabChange)onTabChange(vehicle.id,next);
    return next;
  });
  const hintColorFor=key=>{const a=alerts.find(x=>x.target===key);return a?a.color:T.teal;};

  return(
    <>
    <div className="hf-vcard">
      <div className="hf-vcard-id">
        <div className="hf-vphoto">🚗</div>
        <div className="hf-vid-main" style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15.5,fontWeight:800,color:T.text,marginBottom:2}}>{vehicle.nickname}</div>
          <div className="hf-vsub">
            <span>{vehicle.year} {vehicle.make} {vehicle.model}{vehicle.trim?` ${vehicle.trim}`:""}</span>
            <span className="dot">·</span>
            <MileageUpdater vehicle={vehicle} onUpdate={patch=>onUpdate(vehicle.id,patch)}/>
            <span className="dot">·</span>
            <span className="mono">{vehicle.vin||"—"}</span>
            {alerts.length>0&&<span className="dot">·</span>}
            {alerts.map((a,i)=>(
              <span key={i} className="hf-alert-badge" style={{background:a.bg,color:a.color}} data-tip={a.tip}
                onMouseEnter={()=>setHintTab(a.target)} onMouseLeave={()=>setHintTab(null)}>{a.label}</span>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:6,flexShrink:0}}>
          <button onClick={()=>setEditOpen(true)} className="hf-icon-btn" title="Edit vehicle">✏️</button>
          <button onClick={()=>setEditOpen(true)} className="hf-icon-btn danger" title="Remove vehicle">🗑️</button>
        </div>
      </div>

      <div className="hf-sec-tabs">
        {SECTIONS.map(s=>(
          <button key={s.key}
            className={`hf-sec-tab${activeTab===s.key?" active":""}${hintTab===s.key?" hint":""}`}
            style={hintTab===s.key?{"--hf-hint-color":hintColorFor(s.key)}:undefined}
            onClick={()=>openTab(s.key)}>{s.label}</button>
        ))}
      </div>

      {activeTab==="financial"&&(
        <div className="hf-sec-panel">
          <SectionLabel>Financial</SectionLabel>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <EnrichPanel vehicleId={vehicle.id} mode={mode} isExample={!!vehicle.isExample} onEnrich={onEnrich}/>

            <div className="hf-fin-strip">
              <button className={`hf-fin-cell${insSave>0?" has-save":""}`} onClick={()=>setFinOpen(o=>o==="insurance"?null:"insurance")}>
                <span style={{fontSize:9.5,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:".04em"}}>Insurance</span>
                <span className="mono" style={{fontSize:13,fontWeight:800,color:insSave>0?T.green:T.text}}>{insSave>0?"+"+fmt(insSave)+"/mo":fmt(estInsurance)+"/mo"}<span style={{fontSize:9,fontWeight:600,color:T.textSm}}>*</span></span>
              </button>
              <button className={`hf-fin-cell${refiSave>0?" has-save":""}`} onClick={()=>hasLoan&&setFinOpen(o=>o==="refi"?null:"refi")}>
                <span style={{fontSize:9.5,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:".04em"}}>Refinance</span>
                <span className="mono" style={{fontSize:13,fontWeight:800,color:!hasLoan?T.teal:refiSave>0?T.green:T.text}}>{!hasLoan?"No loan":refiSave>0?<>+{fmt(refiSave)}/mo<span style={{fontSize:9,fontWeight:600,color:T.textSm}}>*</span></>:<>Best rate<span style={{fontSize:9,fontWeight:600,color:T.textSm}}>*</span></>}</span>
              </button>
              <button className="hf-fin-cell has-save" onClick={()=>resolvedBestOffer&&setFinOpen(o=>o==="sell"?null:"sell")}>
                <span style={{fontSize:9.5,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:".04em"}}>Sell Offer</span>
                <span className="mono" style={{fontSize:13,fontWeight:800,color:T.teal}}>{resolvedBestOffer?<>{fmt(resolvedBestOffer.amount)}<span style={{fontSize:9,fontWeight:600,color:T.textSm}}>*</span></>:"—"}</span>
              </button>
            </div>

            {finOpen==="insurance"&&(
              <div className="hf-fin-detail">
                {insSave>0?<>Currently paying <b style={{color:T.text}}>{fmt(estInsurance)}/mo</b>. Best matched quote is <b style={{color:T.text}}>{fmt(resolvedInsurance.amount)}/mo</b>* — that's <b style={{color:T.text}}>{fmt(insSave*12)}/yr</b> back.</>
                :resolvedInsurance?<>Currently paying <b style={{color:T.text}}>{fmt(estInsurance)}/mo</b>, already the best quote* found for this vehicle.</>
                :<>No insurance quote on file — run a lookup above to get one.</>}
              </div>
            )}
            {finOpen==="refi"&&hasLoan&&(
              <div className="hf-fin-detail">
                {refiSave>0?<>Current rate <b style={{color:T.text}}>{num(vehicle.rate).toFixed(1)}% APR</b> vs. a resolved <b style={{color:T.text}}>{resolvedRefiRate.amount.toFixed(1)}% APR</b>* offer — save <b style={{color:T.text}}>{fmt(refiSave)}/mo</b> ({fmt(refiSave*12)}/yr).</>
                :<>Current rate <b style={{color:T.text}}>{estRate.toFixed(1)}% APR</b> is already competitive against available offers*.</>}
                {fredRate&&<div style={{fontSize:10,color:T.textSm,marginTop:6}}>National average (FRED): {fredRate.rate.toFixed(2)}% as of {fredRate.asOf} — not adjusted for credit</div>}
              </div>
            )}
            {finOpen==="sell"&&resolvedBestOffer&&(
              <div className="hf-fin-detail">
                Best matched offer <b style={{color:T.text}}>{fmt(resolvedBestOffer.amount)}</b>*, vs. adjusted value {fmt(adjValue)}. Net after loan payoff: <b style={{color:T.green}}>{fmt(net)}</b>.
                <button onClick={()=>setSellOpen(true)} style={{display:"block",marginTop:8,background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:8,padding:"9px 0",width:"100%",fontSize:13,fontWeight:700}}>Sell Now — Live Bids</button>
              </div>
            )}
            <div style={{fontSize:10,color:T.textSm,lineHeight:1.4}}>*Simulated estimate based on AI research — real partner offers aren't available in all areas yet.</div>

            <div>
              <SectionLabel>Financial Snapshot</SectionLabel>
              <div className="hf-grid3" style={{gap:14,padding:"14px 16px",background:T.slateL,borderRadius:12,border:`1px solid ${T.border}`}}>
                {vehicle.financials?.value?(
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    <span style={{fontSize:11,fontWeight:600,color:T.textSm,textTransform:"uppercase",letterSpacing:".06em"}}>Market Value</span>
                    <div style={{display:"flex",alignItems:"center",gap:8}}><span className="mono" style={{fontSize:15,fontWeight:700,color:T.text}}>{fmt(resolvedValue.amount)}</span><ProvenanceBadge source={resolvedValue.source}/></div>
                    <span style={{fontSize:10,color:T.textSm}}>{resolvedValue.source}{resolvedValue.origin?` — ${resolvedValue.origin}`:""}</span>
                    {vehicle.financials.value.fetchedAt&&<span style={{fontSize:10,color:T.textSm}}>Updated {agoLabel(vehicle.financials.value.fetchedAt)}</span>}
                  </div>
                ):(
                  <>
                    <MetaChip label="Base Value" value={fmt(vehicle.value)}/>
                    {adjDiff!==0?<div style={{display:"flex",flexDirection:"column",gap:4}}><span style={{fontSize:11,fontWeight:600,color:T.textSm,textTransform:"uppercase",letterSpacing:".06em"}}>Mileage Adj.</span><span className="mono" style={{fontSize:15,fontWeight:700,color:adjDiff<0?T.red:T.green}}>{adjDiff>0?"+":""}{fmt(adjDiff)}</span><span style={{fontSize:10,color:T.textSm}}>{adjDiff<0?"Above avg":"Below avg"}</span></div>:<MetaChip label="Mileage Adj." value="Avg. mileage"/>}
                    <MetaChip label="Adj. Value (est.)" value={hasValue?fmt(adjValue):"—"} valueColor={adjDiff<0?T.amber:T.green}/>
                  </>
                )}
                <MetaChip label="Loan Balance" value={fmt(vehicle.loan)}/>
                <MetaChip label="Equity" value={equity!=null?fmt(equity):"—"} valueColor={equity==null?T.text:equity>=0?T.green:T.red}/>
                <MetaChip label={paymentKnown?"Monthly Pmt":"Monthly Pmt (est.)"} value={hasLoan?fmt(estPayment):"—"}/>
                <MetaChip label={rateKnown?"Interest Rate":"Interest Rate (est.)"} value={hasLoan?estRate.toFixed(1)+"%":"—"} valueColor={estRate>6.5&&hasLoan?T.amber:T.text}/>
                <MetaChip label={insuranceKnown?"Insurance/mo":"Insurance/mo (est.)"} value={fmt(estInsurance)}/>
              </div>
            </div>

            <div>
              <SectionLabel>Monthly Cost of Ownership</SectionLabel>
              <div style={{padding:"14px 16px",background:T.slateL,borderRadius:12,border:`1px solid ${T.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12,paddingBottom:12,borderBottom:`1px solid ${T.border}`}}>
                  <span style={{fontSize:13,fontWeight:700,color:T.text}}>Total</span>
                  <span className="mono" style={{fontSize:20,fontWeight:800,color:T.teal}}>{tco.total!=null?fmt(tco.total):"—"}/mo</span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.textMd}}><span>Loan payment</span><span className="mono">{fmt(tco.payment)}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.textMd}}><span>Insurance</span><span className="mono">{tco.insurance!=null?fmt(tco.insurance):"—"}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.textMd}}><span>Fuel</span><span className="mono">{tco.fuel!=null?fmt(tco.fuel):"—"}</span></div>
                  <div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:T.textMd}}>
                      <span style={{display:"flex",alignItems:"center",gap:6}}>Maintenance<ProvenanceBadge source="estimated"/></span>
                      <span className="mono">{tco.maintenance!=null?fmt(tco.maintenance):"—"}</span>
                    </div>
                    <div style={{fontSize:10,color:T.textSm,marginTop:2}}>Estimated — oil, tire rotation, and brake service only; doesn't cover the full maintenance schedule</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <SectionLabel>Details</SectionLabel>
              <div className="hf-grid3" style={{gap:10,padding:"14px 16px",background:T.slateL,borderRadius:12,border:`1px solid ${T.border}`}}>
                {[["Plate",vehicle.plate||"—",true],["Color",vehicle.color||"—",false],["Driver",vehicle.driver||"—",false]].map(([l,v,mono])=>(
                  <div key={l}><div style={{fontSize:11,color:T.textSm,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",marginBottom:3}}>{l}</div><div className={mono?"mono":""} style={{fontSize:13,fontWeight:600,color:T.text}}>{v}</div></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab==="safety"&&(
        <div className="hf-sec-panel">
          <SectionLabel>Safety & Recalls</SectionLabel>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <SafetyProfile vehicle={vehicle}/>
            <RecallAndService vehicle={vehicle} mode={mode}/>
            {onServiceSchedule&&<button onClick={()=>onServiceSchedule(vehicle)} style={{width:"100%",background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:9,padding:"10px 0",fontSize:13,fontWeight:700}}>Schedule Service →</button>}
          </div>
        </div>
      )}

      {activeTab==="fuel"&&(
        <div className="hf-sec-panel">
          <SectionLabel>Fuel Economy {mpg&&<ModeBadge mode={mpg.source}/>}</SectionLabel>
          <div style={{padding:"14px 16px",background:T.slateL,borderRadius:12,border:`1px solid ${T.border}`}}>
            {mpgLoading&&<div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:T.textSm}}><Spin/>Loading EPA data...</div>}
            {!mpgLoading&&mpgError&&<div style={{fontSize:12,color:T.red,background:T.redL,borderRadius:8,padding:"9px 12px",display:"flex",flexDirection:"column",gap:6}}><span>{mpgError}</span><button onClick={()=>{setMpgError("");setMpgLoading(true);fetchFuelEconomy(vehicle.year,vehicle.make,vehicle.model).then(data=>{setMpg(data);setMpgLoading(false);}).catch(e=>{setMpgError(e.message||"Fuel economy lookup failed");setMpgLoading(false);});}} style={{alignSelf:"flex-start",fontSize:11,fontWeight:700,color:T.red,background:"none",border:"1px solid currentColor",borderRadius:6,padding:"3px 9px",cursor:"pointer"}}>Retry</button></div>}
            {!mpgLoading&&!mpgError&&!mpg&&<div style={{fontSize:13,color:T.textSm}}>No fuel economy data available.</div>}
            {mpg&&<div className="hf-grid3" style={{gap:14}}>
              <MetaChip label="City" value={mpg.city?`${mpg.city} mpg`:"—"}/>
              <MetaChip label="Highway" value={mpg.hwy?`${mpg.hwy} mpg`:"—"}/>
              <MetaChip label="Combined" value={mpg.comb?`${mpg.comb} mpg`:"—"} valueColor={T.teal}/>
              {mpg.annualFuelCost&&<div style={{gridColumn:"span 2"}}><MetaChip label="Est. Annual Fuel Cost" value={fmt(mpg.annualFuelCost)} valueColor={T.amber}/></div>}
              {mpg.fuelType&&<MetaChip label="Fuel Type" value={mpg.fuelType}/>}
              {gasPrice&&mpg.comb&&<div style={{gridColumn:"span 3"}}>
                <MetaChip label="Cost per Mile" value={`$${(gasPrice.price/mpg.comb).toFixed(3)}`} valueColor={T.amber}/>
                <div style={{fontSize:10,color:T.textSm,marginTop:4}}>Gas: ${gasPrice.price.toFixed(2)}/gal, {gasPrice.region} avg (EIA) as of {gasPrice.asOf}</div>
              </div>}
            </div>}
          </div>
        </div>
      )}

      {activeTab==="dates"&&(
        <div className="hf-sec-panel">
          <SectionLabel>Dates & Coverage</SectionLabel>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {[{l:"Registration Expires",date:vehicle.regExp,days:daysReg,warn:60,urgent:30},{l:"Warranty Ends",date:vehicle.warrantyExp,days:daysWar,warn:120,urgent:60}].map(row=>(
              <div key={row.l} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"13px 16px",borderRadius:10,background:T.slateL,border:`1px solid ${T.border}`}}>
                <span style={{fontSize:13,fontWeight:600,color:T.text}}>{row.l}</span>
                <div style={{textAlign:"right"}}>
                  <div className="mono" style={{fontSize:13,fontWeight:600,color:T.text}}>{row.date||"—"}</div>
                  {row.days!==null&&<div style={{fontSize:12,marginTop:2,fontWeight:600,color:row.days<=0?T.red:row.days<=row.urgent?T.red:row.days<=row.warn?T.amber:T.green}}>{row.days<=0?"Expired":`${row.days} days left`}</div>}
                  {!row.date&&<div style={{fontSize:12,color:T.textSm}}>Not set</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab==="maint"&&(
        <div className="hf-sec-panel">
          <SectionLabel>Maintenance</SectionLabel>
          <MaintenanceSchedule
            vehicleId={vehicle.id}
            make={vehicle.make}
            model={vehicle.model}
            year={vehicle.year}
            currentMileage={num(vehicle.mileage)}
            startExpanded
            onSchedule={()=>onServiceSchedule&&onServiceSchedule(vehicle)}
          />
        </div>
      )}
    </div>
    {sellOpen&&<BidModal vehicle={vehicle} onClose={()=>setSellOpen(false)}/>}
    {editOpen&&<VehicleModal mode={mode} existing={vehicle} onSave={async v=>{const ok=v?await onUpdate(vehicle.id,v):await onDelete(vehicle.id);if(ok)setEditOpen(false);}} onClose={()=>setEditOpen(false)}/>}
    </>
  );
}
